import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Grid,
    Button,
    Select, // Kept for small, static dropdowns (Employment Type, Compliance)
    MenuItem,
    InputLabel,
    FormControl,
    FormHelperText,
    Autocomplete, Paper, Snackbar, Alert, Modal, LinearProgress
} from '@mui/material';
import axios from 'axios';
// NOTE: Assuming path to your axios utility and toast library are correct
import { axiosInstance, localAxiosGet, localAxiosPut } from '../../../../Axios/axios';
import { toast } from 'react-toastify';
import TokenExpirePopup from '../../../../components/TokenExpirePopup';
import { useNavigate } from 'react-router-dom';

// --- Helper function for safely formatting dates ---
const formatDateForInput = (dateString) => {
    return dateString ? dateString.split('T')[0] : '';
};


const EmploymentDetails = ({
    userId,
    organizationId,
    initialData,
    navigationContext,
    onUpdateEmployeeSuccess,
    onSaveAndReturn,
    onFormCompletionChange
}) => {
    // 1. Local State for Form Data
    const [formData, setFormData] = useState({
        dateOfJoining: '',
        employmentTypeId: '',
        department: '',
        team: '',
        designation: '',
        reportingManager: '',
        workLocation: '',
        roleId: '',
        employmentTypeName: ''
    });

    // 2. Local States for Dropdown Options
    const [departments, setDepartments] = useState([]);
    const [teams, setTeams] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [locations, setLocations] = useState([]);
    const [managerOptions, setManagerOptions] = useState([]);
    const [rolesNamesList, setRolesNamesList] = useState([]); // Array of { id, name }
    const [employmentTypeOptions, setEmploymentTypeOptions] = useState([])

    // 3. Loading States
    const [loadingDropdowns, setLoadingDropdowns] = useState(false);
    const [loadingTeams, setLoadingTeams] = useState(false);
    const [loadingDesignations, setLoadingDesignations] = useState(false);
    const token = localStorage.getItem('accessToken');
    const [completion, setCompletion] = useState(0);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [openTokenExpirePopup, setOpenTokenExpirePopup] = useState(false);
    const navigate = useNavigate()
    // --- Helper to find the option object from ID for Autocomplete's 'value' prop ---
    const getOptionById = (optionsList, id) => {
        // Autocomplete expects the actual object if the value is an object, 
        // or a string/number if the value is a scalar (controlled mode)
        return optionsList.find(option => option.id === id) || null;
    };
    const handleCloseTokenExpirePopup = () => {
        setOpenTokenExpirePopup(false);
        navigate('/');
    };

    // 4. API Fetch Functions (Unchanged - they return { id, name } format)

    const fetchRolesList = useCallback(async () => {
        try {

            const response = await axiosInstance(localAxiosGet(`/organization/getAllRolesByOrgId?organization_id=${organizationId}`, token));

            if (response.data && response.data.result && Array.isArray(response.data.result)) {
                const mappedRoles = response.data.result.map(role => ({
                    id: role.id,
                    name: role.name // Using role_name here based on typical API structure for roles, checking against previous code's assumption: role.name
                }));
                setRolesNamesList(mappedRoles);
            } else {
                setRolesNamesList([]);
            }
        } catch (error) {
            console.error('Error in fetching roles dropdown:', error);
            toast.error('Failed to load roles list.');
            setRolesNamesList([]);
        }
    }, [organizationId]);

    const fetchReportingManagersList = useCallback(async () => {
        try {

            const response = await axiosInstance(localAxiosGet(`/project/getAllTeamLeadOrManager?organization_id=${organizationId}&is_manager=true&is_team_lead=true`, token));

            if (response.data && response.data.message && response.data.message.users) {
                const options = response.data.message.users.map(user => ({
                    id: user.id,
                    name: `${user.first_name} ${user.last_name} (${user.user_type})`,
                    userType: user.user_type
                }));
                setManagerOptions(options);
            }
        } catch (error) {
            console.error('Error fetching reporting managers:', error);
            toast.error('Failed to load reporting managers.');
            setManagerOptions([]);
        }
    }, [organizationId]);

    const fetchLocationsByOrganizationId = useCallback(async (orgId) => {
        try {

            const response = await axiosInstance(localAxiosGet(`/organization/getAllLocationsByOrgId/${orgId}`, token));

            if (response.data && response.data.message) {
                const locationsArray = response.data.message.map(location => ({
                    id: location.id,
                    // Combine name and city for better search display
                    name: `${location.location_name} (${location.city}, ${location.country})`,
                    location_name: location.location_name,
                    country: location.country,
                    city: location.city
                }));
                setLocations(locationsArray);
            } else {
                setLocations([]);
            }
        } catch (error) {
            console.error('Error fetching locations:', error);
            toast.error('Failed to load locations.');
            setLocations([]);
        }
    }, []);

    const fetchDepartmentsByOrganizationId = useCallback(async (orgId) => {
        try {

            const response = await axiosInstance(localAxiosGet(`/organization/getAllDepartmentsByOrganizationId/${orgId}`, token));

            if (response.data && response.data.message && response.data.message.departments) {
                const departmentsArray = response.data.message.departments.map(dept => ({
                    id: dept.id,
                    name: dept.department
                }));
                setDepartments(departmentsArray);
            } else {
                setDepartments([]);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
            toast.error('Failed to load departments.');
            setDepartments([]);
        }
    }, []);

    const fetchTeamsByDepartmentId = useCallback(async (departmentId) => {
        if (!departmentId) {
            setTeams([]);
            setDesignations([]);
            return;
        }
        setLoadingTeams(true);
        try {

            const response = await axiosInstance(localAxiosGet(`/organization/getAllTeamsByDepartmentId/${departmentId}`, token));

            if (response.data && response.data.message && response.data.message.teams) {
                const teamsArray = response.data.message.teams.map(team => ({
                    id: team.id,
                    name: team.team
                }));
                setTeams(teamsArray);
            } else {
                setTeams([]);
            }
        } catch (error) {
            console.error('Error fetching teams:', error);
            toast.error('Failed to load teams.');
            setTeams([]);
        } finally {
            setLoadingTeams(false);
        }
    }, []);

    const fetchDesignationsByTeamId = useCallback(async (teamId) => {
        if (!teamId) {
            setDesignations([]);
            return;
        }
        setLoadingDesignations(true);
        try {

            const response = await axiosInstance(localAxiosGet(`/organization/getAllDesignationsByTeamId/${teamId}`, token));

            if (response.data && response.data.message && response.data.message.designations) {
                const designationsArray = response.data.message.designations.map(designation => ({
                    id: designation.id,
                    name: designation.designation
                }));
                setDesignations(designationsArray);
            } else {
                setDesignations([]);
            }
        } catch (error) {
            console.error('Error fetching designations:', error);
            toast.error('Failed to load designations.');
            setDesignations([]);
        } finally {
            setLoadingDesignations(false);
        }
    }, []);

    const fetchEmployementType = useCallback(async () => {
        try {
            const response = await axiosInstance(localAxiosGet(`/hr/getAllEmploymentTypes/${organizationId}`, token));
            if (response.data.status === 'SUCCESS') {
                setEmploymentTypeOptions(response.data.result);
            } else {
                setSnackbar({
                    open: true,
                    message: response.data.message || 'Error while fectching details',
                    severity: 'error'
                })
            }

        } catch (error) {
            console.log('Error in fetching Employement type details', error);
            setSnackbar({
                open: true,
                message: error?.data?.message || 'Error while fectching details',
                severity: 'error'
            })
        }
    })

    // 5. useEffect for Initial Data Loading and Initial Dropdown Fetches
    useEffect(() => {
        const loadInitialData = async () => {
            // 1. Fetch Static Dropdowns first
            setLoadingDropdowns(true);
            await Promise.all([
                fetchDepartmentsByOrganizationId(organizationId),
                fetchLocationsByOrganizationId(organizationId),
                fetchReportingManagersList(),
                fetchRolesList(),
                fetchEmployementType()
            ]);
            setLoadingDropdowns(false);

            // 2. Load Initial Data and Trigger Cascading Fetches
            if (initialData && organizationId) {
                // Set initial form state. department_id etc. will be set as IDs.
                setFormData(prev => ({
                    ...prev,
                    dateOfJoining: formatDateForInput(initialData.doj),
                    employmentTypeId: initialData.employment_type_id,
                    department: initialData.department_id || '',
                    team: initialData.team_id || '',
                    designation: initialData.designation_id || '',
                    reportingManager: initialData.report_manager_id || '',
                    workLocation: initialData.location_id || '',
                    roleId: initialData.role_id || '',

                }));

                // Trigger cascading fetches using initial values
                if (initialData.department_id) {
                    fetchTeamsByDepartmentId(initialData.department_id);
                }
                if (initialData.team_id) {
                    fetchDesignationsByTeamId(initialData.team_id);
                }
            }
        };

        if (organizationId) {
            loadInitialData();
        }
    }, [initialData, organizationId, fetchDepartmentsByOrganizationId, fetchLocationsByOrganizationId, fetchReportingManagersList, fetchRolesList, fetchTeamsByDepartmentId, fetchDesignationsByTeamId]);


    const calculateProgress = useCallback(() => {
        const allFields = [
            'dateOfJoining',
            'employmentTypeId',
            'department',
            'team',
            'designation',
            'reportingManager',
            'workLocation',
            'roleId'
        ];

        const filledCount = allFields.filter(
            field => formData[field]?.toString().trim() !== ''
        ).length;

        const percentage = Math.round((filledCount / allFields.length) * 100);
        setCompletion(percentage);
    }, [formData]);

   useEffect(() => {
  calculateProgress();
  if (typeof onFormCompletionChange === 'function') {
    onFormCompletionChange(completion);
  }
}, [calculateProgress, completion]);


    // 6. Handle standard input changes (e.g., TextField, static Select)
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // 6b. Handle Autocomplete changes, including cascading logic
    const handleAutocompleteChange = (name, value) => {
        // value is the selected object { id, name, ... } or null
        const id = value ? value.id : '';

        setFormData(prevData => ({
            ...prevData,
            [name]: id
        }));

        // Handle cascading dropdowns
        if (name === 'department') {
            setFormData(prevData => ({ ...prevData, team: '', designation: '' }));
            fetchTeamsByDepartmentId(id);
            setDesignations([]); // Clear designations immediately
        } else if (name === 'team') {
            setFormData(prevData => ({ ...prevData, designation: '' }));
            fetchDesignationsByTeamId(id);
        }
    };

    const updateEmployeeTypeData = async () => {
        const payload = {
            employment_type_id: formData.employmentTypeId
        }
        try {
            const response = await axiosInstance(localAxiosPut(`/hr/updateEmploymentType/${userId}`, payload, token));
            if (response.data.status === 'SUCCESS') {
                setSnackbar({
                    open: true,
                    message: response.data?.message?.msg || 'Updated successfully',
                    severity: 'success'
                })
            } else {
                setSnackbar({
                    open: true,
                    message: response.data?.message?.msg || 'Error in updating the details',
                    severity: 'error'
                })
            }
        } catch (error) {
            console.error('API Save Error:', error);
            setSnackbar({
                open: true,
                message: error.data?.message?.msg || 'Error while updating the details',
                severity: 'error'
            })
        }
    }
    // 7. Handle form submission (Save Changes)
    const handleSaveChanges = async () => {

        if (
            !formData.reportingManager ||
            !formData.dateOfJoining ||
            !formData.employmentTypeId ||
            !formData.department ||
            !formData.designation
        ) {
            setSnackbar({
                open: true,
                message: 'Required Fields are missing',
                severity: 'error'
            });
            return;
        }

        if (initialData.employment_type_id !== formData.employmentTypeId) {
            await updateEmployeeTypeData();
        }

        const payload = {
            report_manager_id: formData.reportingManager,
            department_id: formData.department,
            team_id: formData.team,
            designation_id: formData.designation,
            doj: formData.dateOfJoining,
            role_id: formData.roleId,
            location_id: formData.workLocation
        }
        try {
            const response = await axiosInstance(localAxiosPut(`/organization/updateEmployeeDetails?userId=${userId}`, payload, token));
            if (response.data.status === 'SUCCESS') {
                setSnackbar({
                    open: true,
                    message: response.data?.message?.msg || 'Updated successfully',
                    severity: 'success'
                })
                onUpdateEmployeeSuccess();
                if (navigationContext?.returnToRehire && onSaveAndReturn) {
                    onSaveAndReturn();
                }

                return true;
            } else {
                setSnackbar({
                    open: true,
                    message: response.data?.message?.msg || 'Error in updating the details',
                    severity: 'error'
                })
                return false;
            }
        } catch (error) {
            console.error('API Save Error:', error);
            setSnackbar({
                open: true,
                message: error.data?.message?.msg || 'Error while updating the details',
                severity: 'error'
            })
        }
        if (navigationContext?.returnToRehire && onSaveAndReturn) {
            onSaveAndReturn();
        }

    };
    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Style helper for subheadings
    const SubHeading = ({ children }) => (
        <Typography
            variant="h5"
            sx={{
                mt: 2,
                mb: 2,
                pb: 0.5,
                borderBottom: '2px solid #ddd',
                fontWeight: 700
            }}
        >
            {children}
        </Typography>
    );

    if (loadingDropdowns) {
        return <Box sx={{ p: 3, textAlign: 'center' }}>Loading required options... </Box>;
    }

    return (
        <Box>
            {/* <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                    fontWeight: 700, 
                    mb: 3, 
                    color: 'primary.main'
                }}
            >
                Employee Work Profile
            </Typography> */}

            <Grid container spacing={3}>

                {/* ------------------------------------------- */}
                {/* Section 1: Employment Details */}
                {/* ------------------------------------------- */}
                <Grid item xs={12}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <SubHeading>Employment Details</SubHeading>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Form Completion: {completion}%
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={completion}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    backgroundColor: '#eee',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 5,
                                        backgroundColor: completion === 100 ? '#2ecc71' : '#1976d2'
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </Grid>

                {/* Row 1: Date of Joining, Employment Type, System Role */}
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Date of Joining"
                        name="dateOfJoining"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.dateOfJoining}
                        onChange={handleInputChange}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth required>
                        <InputLabel>Employment Type</InputLabel>
                        <Select
                            label="Employment Type"
                            name="employmentTypeId"
                            value={formData.employmentTypeId}
                            onChange={(e) => {
                                const selectedId = e.target.value;
                                const selectedOption = employmentTypeOptions.find(option => option.id === parseInt(selectedId));

                                setFormData(prevData => ({
                                    ...prevData,
                                    employmentTypeId: selectedId,
                                    employmentTypeName: selectedOption ? selectedOption.name : ''
                                }));
                            }}
                        >
                            {employmentTypeOptions.map(option => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                    {/* 💡 Autocomplete for System Role */}
                    <Autocomplete
                        fullWidth
                        options={rolesNamesList}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={getOptionById(rolesNamesList, formData.roleId)} // Match by ID
                        onChange={(event, newValue) => {
                            handleAutocompleteChange('roleId', newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="User Role"
                                required
                                helperText={rolesNamesList.length === 0 ? 'Roles loading or empty.' : 'Sets the user\'s access privileges.'}
                            />
                        )}
                    />
                </Grid>

                {/* Row 2: Department, Team, Designation (Cascading Autocompletes) */}
                <Grid item xs={12} sm={4}>
                    {/* 💡 Autocomplete for Department */}
                    <Autocomplete
                        fullWidth
                        options={departments}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={getOptionById(departments, formData.department)}
                        onChange={(event, newValue) => {
                            handleAutocompleteChange('department', newValue);
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Department" required />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    {/* 💡 Autocomplete for Team (Depends on Department) */}
                    <Autocomplete
                        fullWidth
                        options={teams}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={getOptionById(teams, formData.team)}
                        onChange={(event, newValue) => {
                            handleAutocompleteChange('team', newValue);
                        }}
                        disabled={loadingTeams || departments.length === 0}
                        loading={loadingTeams}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Team"
                                helperText={loadingTeams ? 'Fetching teams...' : ''}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    {/* 💡 Autocomplete for Designation (Depends on Team) */}
                    <Autocomplete
                        fullWidth
                        options={designations}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={getOptionById(designations, formData.designation)}
                        onChange={(event, newValue) => {
                            handleAutocompleteChange('designation', newValue);
                        }}
                        disabled={loadingDesignations || teams.length === 0}
                        loading={loadingDesignations}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Designation"
                                required
                                helperText={loadingDesignations ? 'Fetching designations...' : ''}
                            />
                        )}
                    />
                </Grid>

                {/* Row 3: Reporting Manager, Work Location */}
                <Grid item xs={12} sm={6}>
                    {/* 💡 Autocomplete for Reporting Manager */}
                    <Autocomplete
                        fullWidth
                        options={managerOptions}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={getOptionById(managerOptions, formData.reportingManager)}
                        onChange={(event, newValue) => {
                            handleAutocompleteChange('reportingManager', newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Reporting Manager"
                                required
                                helperText="Select the Reporting Manager or Team Lead"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    {/* 💡 Autocomplete for Work Location */}
                    <Autocomplete
                        fullWidth
                        options={locations}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={getOptionById(locations, formData.workLocation)}
                        onChange={(event, newValue) => {
                            handleAutocompleteChange('workLocation', newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Work Location"
                                required
                            />
                        )}
                    />
                </Grid>

            </Grid>

            {/* Save Changes Button at the bottom */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                {navigationContext?.returnToRehire ? (
                    <Paper
                        elevation={3}
                        sx={{
                            width: '100%',
                            p: 2,
                            bgcolor: 'info.light',
                            borderLeft: '4px solid',
                            borderColor: 'info.main'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Rehire Verification in Progress
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Completing verification for: <strong>{navigationContext.stepLabel || 'Personal & Contact Details'}</strong>
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="info"
                                onClick={handleSaveChanges}
                            >
                                Verify & Return to Rehire
                            </Button>
                        </Box>
                    </Paper>
                ) : (
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ py: 1.5, px: 4 }}
                        onClick={handleSaveChanges}
                    >
                        Save Changes
                    </Button>
                )}
            </Box>

            <Modal
                open={openTokenExpirePopup}
                onClose={handleCloseTokenExpirePopup}
                aria-labelledby="token-expire-modal-title"
                aria-describedby="token-expire-modal-description"
            >
                <TokenExpirePopup onClose={handleCloseTokenExpirePopup} />
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EmploymentDetails;